import { UploadOutlined } from '@ant-design/icons';
import { message, Upload, type UploadProps } from 'antd';
import { compact } from 'lodash/fp';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import getApolloErrors from '../../../../server/utils/getApolloErrors';
import { useImportVariantImagesMutation } from '../../../api/mutations/importVariantImages';
import { useThemeComponents } from '../../../themes';
import { allowedExtensions, validateExtensions } from '../../../utilities/extensions';

type ImportImagesProps = {
    disabled?: boolean;
    moduleId: string;
    onDone?: () => void;
};
const ImportImages = ({ disabled, moduleId, onDone }: ImportImagesProps) => {
    const { t } = useTranslation('variantList');
    const { Button } = useThemeComponents();

    const accept = useMemo(() => [...allowedExtensions.image, ...allowedExtensions.archive].join(','), []);

    const [importVariantImagesMutation] = useImportVariantImagesMutation();

    const [importFiles, setImportFiles] = useState<File[]>([]);

    const customRequest = useCallback<UploadProps['customRequest']>(async () => {
        if (importFiles?.length > 0) {
            try {
                const validatingFiles = await Promise.all(
                    importFiles.map(f => {
                        const reader = new FileReader();
                        const promise = new Promise<File>((resolve, reject) => {
                            reader.onload = async event => {
                                const content = event.target.result as string;
                                const validationResult = await validateExtensions(content, f.name);
                                if (validationResult.valid) {
                                    return resolve(f);
                                }

                                return resolve(null);
                            };

                            reader.onerror = event => resolve(null);
                        });
                        reader.readAsBinaryString(f);

                        return Promise.resolve(promise);
                    })
                );
                const validatedFiles = compact(validatingFiles);
                if (!validatedFiles.length) {
                    message.error(t('common:upload.messages.fileError'));
                }
                message.loading({
                    content: t('variantList:messages.imagesUploading'),
                    duration: 0,
                    key: 'primary',
                });
                const { data } = await importVariantImagesMutation({
                    variables: {
                        uploads: validatedFiles,
                        vehicleModuleId: moduleId,
                    },
                }).finally(() => {
                    message.destroy('primary');
                    if (onDone) {
                        onDone();
                    }
                });
                if (data?.updatedCount > 0) {
                    message.success({
                        content: t('variantList:messages.uploadSuccessful.message', { count: data?.updatedCount }),
                        key: 'primary',
                    });
                }
            } catch (error) {
                const apolloErrors = getApolloErrors(error);
                if (apolloErrors?.$root) {
                    message.error(apolloErrors?.$root);
                }
            }
        }
    }, [t, moduleId, importFiles, onDone, importVariantImagesMutation]);

    const beforeUpload = useCallback<UploadProps['beforeUpload']>(async (file, fileList) => {
        if (fileList?.length && file.uid === fileList[fileList.length - 1].uid) {
            setImportFiles(fileList);

            return true;
        }

        return false;
    }, []);

    return (
        <Upload
            accept={accept}
            beforeUpload={beforeUpload}
            customRequest={customRequest}
            disabled={disabled}
            showUploadList={false}
            multiple
        >
            <Button disabled={disabled} icon={<UploadOutlined />} type="primary">
                {t('variantList:importButton')}
            </Button>
        </Upload>
    );
};

export default ImportImages;
